import { ApolloClient } from "@apollo/client";
import { getOperationName } from "@apollo/client/utilities";
import { parse } from "graphql";
import type { DocumentNode } from "graphql";
import { IMPERSONATION_EXEMPT_OPERATIONS } from "@src/features/app/hooks/useImpersonation";
import {
  holdBackToastsAfterRefusal,
  markImpersonationRefusal,
} from "@src/features/app/utils/toast";
import {
  isImpersonationReadOnlyNow,
  isViewAsEndEvent,
  VIEW_AS_END_EVENT_FLAG,
} from "@src/lib/utils/impersonation";

// While viewing as someone with changes off, no write leaves the browser. This
// is the one place that decides it: ApolloClient.mutate for GraphQL (before any
// optimistic response or cache update, so a control bound to saved data never
// moves) and window.fetch for everything else sent to the API.

/**
 * Mutations that only record that the person read, opened, looked at or is
 * present on something, or that feed analytics. They are dropped without a message:
 * opening a thread or a page is not a change the viewer tried to make, and
 * none of it should be recorded as the person.
 */
export const READ_SIDE_MUTATION_FIELDS: ReadonlySet<string> = new Set([
  "markChatRead",
  "markMessagingActivityItemsRead",
  "createBlastReadRecord",
  "markInProductNotificationsAsRead",
  "markPushNotificationClicked",
  "trackEvent",
  "openedCourse",
  "openedModule",
  "openedSCORMCourse",
  "openedTrainingResource",
  "openedSkill",
  "openedLocation",
  "openedRole",
  "openedUser",
  "openedLocationGroup",
  "openedRoleGroup",
  // Presence and time-on-screen heartbeats, sent on timers.
  "createTrainingSessionEvent",
  "setActiveClient",
  "removeActiveClient",
]);

/**
 * Mutations the server answers without writing while viewing as someone, and
 * whose answer the screen needs: they go through. The get-or-create ones
 * return the person's existing row, or null when nothing was started yet.
 */
export const PASS_THROUGH_MUTATION_FIELDS: ReadonlySet<string> = new Set([
  "getOrCreateCheckInInstanceDraft",
  "getOrCreateTaskListInstanceDraft",
  "getOrCreateAuditInstanceDraft",
  "getOrCreateAssignment",
  "translateText",
  "textToSpeech",
]);

// A blocked write within this long of a click or key press is the viewer's
// doing and gets a message; one without (page-load analytics, a heartbeat) is
// dropped quietly.
const GESTURE_WINDOW_MS = 1000;

let lastGesture: { at: number; target: Element | null } = {
  at: 0,
  target: null,
};

/** Records the control behind the next blocked write. Capture phase. */
export function recordGesture(target: Element | null): void {
  lastGesture = { at: Date.now(), target };
}

type BlockedWriteHandler = (anchor: Element | null) => void;
let blockedWriteHandler: BlockedWriteHandler | null = null;

/** The read-only guard shows the popover or toast for a blocked write. */
export function setBlockedWriteHandler(
  handler: BlockedWriteHandler | null,
): void {
  blockedWriteHandler = handler;
}

const BLOCKED_MESSAGE = "Changes are off while viewing as someone else.";

// Following a link changes nothing; a write that fires as the page changes
// (a "viewed" record) is the page's, not the viewer's.
const NAVIGATION_SELECTOR = "a[href]";

/**
 * Says a write was blocked: nothing for read records; otherwise the popover
 * or toast when the viewer just clicked or pressed a key (not following a
 * link), and quietly when the write is the page's own (a retry, a timer).
 */
function reportBlockedWrite(readSide: boolean): void {
  if (readSide) return;
  // Synchronously, before the caller's own onError shows a generic toast.
  markImpersonationRefusal();
  if (Date.now() - lastGesture.at > GESTURE_WINDOW_MS) return;
  if (lastGesture.target?.closest(NAVIGATION_SELECTOR)) return;
  const anchor = lastGesture.target?.isConnected ? lastGesture.target : null;
  blockedWriteHandler?.(anchor);
}

// What a blocked mutation resolves with, in the shape the server uses for a
// refused MutationResult. Read records report success so fire-and-forget
// callers carry on; every other write reports failure with the read-only
// code, so screens take their usual failure path (nothing saved, nothing
// unhandled) while the popover or toast says why.
function blockedResult(
  document: DocumentNode,
  readSide: boolean,
): { data: Record<string, unknown> } {
  const result = readSide
    ? { success: true, error: null }
    : {
        success: false,
        error: {
          __typename: "Error",
          code: "READ_ONLY_MODE",
          message: BLOCKED_MESSAGE,
        },
      };
  const data: Record<string, unknown> = {};
  for (const definition of document.definitions) {
    if (definition.kind !== "OperationDefinition") continue;
    for (const selection of definition.selectionSet.selections) {
      if (selection.kind !== "Field") continue;
      data[selection.alias?.value ?? selection.name.value] = { ...result };
    }
  }
  return { data };
}

function rootFields(document: DocumentNode): string[] {
  return document.definitions.flatMap((definition) =>
    definition.kind === "OperationDefinition"
      ? definition.selectionSet.selections.flatMap((selection) =>
          selection.kind === "Field" ? [selection.name.value] : [],
        )
      : [],
  );
}

/**
 * The view's own end event: the flag, the TrackEvent name, and a document
 * whose only root field is trackEvent (nothing else rides along).
 */
function isViewAsEndEventDocument(
  flag: unknown,
  operationName: string | null | undefined,
  document: DocumentNode,
): boolean {
  const fields = rootFields(document);
  return (
    isViewAsEndEvent(flag, operationName) &&
    fields.length === 1 &&
    fields[0] === "trackEvent"
  );
}

const allIn = (fields: string[], set: ReadonlySet<string>) =>
  fields.length > 0 && fields.every((field) => set.has(field));

/** Whether a refused write needs no message: read records, pass-throughs. */
export function isQuietMutation(document: DocumentNode): boolean {
  const fields = rootFields(document);
  return (
    allIn(fields, READ_SIDE_MUTATION_FIELDS) ||
    allIn(fields, PASS_THROUGH_MUTATION_FIELDS)
  );
}

/** Refuses mutations on this client while changes are off. */
export function guardMutations<T>(client: ApolloClient<T>): void {
  holdBackToastsAfterRefusal();
  const mutate = client.mutate.bind(client);
  client.mutate = ((options) => {
    const name = getOperationName(options.mutation);
    if (
      !isImpersonationReadOnlyNow() ||
      (name && IMPERSONATION_EXEMPT_OPERATIONS.has(name)) ||
      // The view's own end event, sent as the viewer before leaving.
      isViewAsEndEventDocument(
        (options.context as Record<string, unknown> | undefined)?.[
          VIEW_AS_END_EVENT_FLAG
        ],
        name,
        options.mutation,
      ) ||
      allIn(rootFields(options.mutation), PASS_THROUGH_MUTATION_FIELDS)
    ) {
      return mutate(options);
    }
    const readSide = allIn(
      rootFields(options.mutation),
      READ_SIDE_MUTATION_FIELDS,
    );
    reportBlockedWrite(readSide);
    return Promise.resolve(blockedResult(options.mutation, readSide));
  }) as typeof client.mutate;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

function apiOrigins(): Set<string> {
  const origins = new Set<string>();
  for (const url of [
    process.env.NEXT_PUBLIC_GRAPHQL_URL,
    process.env.NEXT_PUBLIC_API_URL,
  ]) {
    if (!url) continue;
    try {
      origins.add(new URL(url, window.location.origin).origin);
    } catch {
      // A malformed env value guards nothing extra.
    }
  }
  return origins;
}

type GraphQLBody = { query?: string; operationName?: string };

// The operations in a GraphQL POST body (JSON, a batch, or an upload's
// multipart "operations" field), or null when it isn't one.
function graphQLOperations(body: BodyInit | null | undefined): GraphQLBody[] {
  let raw: unknown = body;
  if (typeof FormData !== "undefined" && body instanceof FormData) {
    raw = body.get("operations");
  }
  if (typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw) as GraphQLBody | GraphQLBody[];
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
}

function isAllowedGraphQL(
  body: BodyInit | null | undefined,
  viewAsEndEventFlag: unknown,
): boolean {
  const operations = graphQLOperations(body);
  if (operations.length === 0) return false;
  return operations.every(({ query, operationName }) => {
    if (operationName && IMPERSONATION_EXEMPT_OPERATIONS.has(operationName)) {
      return true;
    }
    let document: DocumentNode;
    try {
      document = parse(query ?? "");
    } catch {
      return false;
    }
    // The view's own end event (the flag rides in the Apollo fetch options).
    if (isViewAsEndEventDocument(viewAsEndEventFlag, operationName, document)) {
      return true;
    }
    const isMutation = document.definitions.some(
      (definition) =>
        definition.kind === "OperationDefinition" &&
        definition.operation === "mutation",
    );
    return (
      !isMutation || allIn(rootFields(document), PASS_THROUGH_MUTATION_FIELDS)
    );
  });
}

let fetchGuarded = false;

/**
 * Refuses non-GET requests to the API while changes are off, as a 403 with
 * the read-only code. GraphQL queries pass; Apollo's mutations never get here.
 */
export function guardFetch(): void {
  if (fetchGuarded || typeof window === "undefined") return;
  fetchGuarded = true;
  const originalFetch = window.fetch.bind(window);
  const origins = apiOrigins();
  window.fetch = (input, init) => {
    const request = input instanceof Request ? input : null;
    const method = (init?.method ?? request?.method ?? "GET").toUpperCase();
    if (!isImpersonationReadOnlyNow() || SAFE_METHODS.has(method)) {
      return originalFetch(input, init);
    }
    const url = new URL(request?.url ?? String(input), window.location.origin);
    const viewAsEndEventFlag = (init as Record<string, unknown> | undefined)?.[
      VIEW_AS_END_EVENT_FLAG
    ];
    if (
      !origins.has(url.origin) ||
      isAllowedGraphQL(init?.body, viewAsEndEventFlag)
    ) {
      return originalFetch(input, init);
    }
    reportBlockedWrite(false);
    return Promise.resolve(
      new Response(JSON.stringify({ code: "impersonation_read_only" }), {
        status: 403,
        headers: { "Content-Type": "application/json" },
      }),
    );
  };
}
